import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import {
  Configuration,
  CreateProfileDto,
  ErrorResponse,
  PaginationMeta,
  UpdateUserDto,
  User,
  UsersApi,
} from '@sr-sdks/api-sdk-axios';
import { resetPassword } from 'aws-amplify/auth';
import { AxiosError } from 'axios';
import { CacheRequestConfig } from 'axios-cache-interceptor';
import { configuration } from '../../configuration';
import { RootState } from '../../stateStore';
import { ApiLoadingStateEnum } from '../../utils/api/apiLoadingStateEnum';
import axiosInstance, {
  invalidateCacheRequestConfig,
} from '../../utils/axiosInstance';
import { ChangePasswordDto } from './changePasswordDto';
import { UserSliceState } from './userSliceState';

// Define interface for paginated users response
interface PaginatedUsers {
  items: User[];
  meta: PaginationMeta;
}

// Function for creating an instance of the UsersApi class
export const GetUsersApi = () => {
  return new UsersApi(
    new Configuration(),
    configuration.PERMISSIONS_API_BASE,
    axiosInstance,
  );
};

// Function for creating an instance of the Axios
const UseAuth0Api = (requestConfig: CacheRequestConfig = {}) => {
  requestConfig.baseURL = configuration.AUTH0_DOMAIN_URL;

  // No cache for Auth0 requests
  requestConfig.cache = false;

  return axiosInstance(requestConfig);
};

const findAll = createAsyncThunk(
  '/v3/user/findAll',
  async (
    {
      externalAuthId,
      page = 1,
      pageSize = 5,
      sortBy = 'firstName:asc,lastName:asc',
      workspaceId,
    }: {
      externalAuthId?: string;
      page?: number;
      pageSize?: number;
      sortBy?: string;
      workspaceId: string;
    },
    thunkApi,
  ) => {
    try {
      const response = await GetUsersApi().usersV2ControllerFindAllV3(
        externalAuthId,
        workspaceId,
        page,
        pageSize,
        sortBy,
      );

      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue(
        (error as AxiosError).response?.data as ErrorResponse,
      );
    }
  },
);

// Get all users not depending on pagination
const findAllUsersByWorkspace = createAsyncThunk(
  '/v3/user/findAllUsersByWorkspace',
  async (
    {
      page = 1,
      pageSize = 50,
      sortBy = 'firstName:asc,lastName:asc',
      workspaceId,
    }: {
      page?: number;
      pageSize?: number;
      sortBy?: string;
      workspaceId: string;
    },
    thunkApi,
  ) => {
    try {
      const response = await GetUsersApi().usersV2ControllerFindAllV3(
        undefined, // externalAuthId
        workspaceId,
        page,
        pageSize,
        sortBy,
      );

      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue(
        (error as AxiosError).response?.data as ErrorResponse,
      );
    }
  },
);

const findMe = createAsyncThunk('v2/user/findMe', async (_, thunkApi) => {
  try {
    const result = await GetUsersApi().usersV2ControllerFindMe();

    return result.data;
  } catch (error) {
    return thunkApi.rejectWithValue(
      (error as AxiosError).response?.data as ErrorResponse,
    );
  }
});

const findOne = createAsyncThunk(
  '/v2/user/findOne',
  async (id: string, thunkApi) => {
    try {
      const result = await GetUsersApi().usersV2ControllerFindOne(id);

      return result.data;
    } catch (error) {
      return thunkApi.rejectWithValue(
        (error as AxiosError).response?.data as ErrorResponse,
      );
    }
  },
);

const updateMe = createAsyncThunk(
  '/v2/user/updateMe',
  async (payload: CreateProfileDto, thunkApi) => {
    try {
      const result = await GetUsersApi().usersV2ControllerCreateMe(
        payload,
        invalidateCacheRequestConfig('user/me'),
      );

      return result.data;
    } catch (error) {
      return thunkApi.rejectWithValue(error as ErrorResponse);
    }
  },
);

const update = createAsyncThunk(
  '/v2/user/update',
  async (
    { id, updateUserDto }: { id: string; updateUserDto: UpdateUserDto },
    thunkApi,
  ) => {
    try {
      const result = await GetUsersApi().usersV2ControllerUpdate(
        id,
        updateUserDto,
        invalidateCacheRequestConfig('user'),
      );

      return result.data;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

const changePassword = createAsyncThunk(
  '/user/changePassword',
  async (emailAddress: string, thunkApi) => {
    // Lowercase for Auth0, which matches case-sensitively. Cognito lookups are
    // case-insensitive, so this is safe for mixed-case Cognito records too.
    const normalizedEmail = emailAddress.trim().toLowerCase();

    if (configuration.COGNITO_ENABLED) {
      try {
        const result = await resetPassword({ username: normalizedEmail });

        return result;
      } catch (error) {
        return thunkApi.rejectWithValue({
          message: 'Unable to send password reset email. Please try again.',
          statusCode: 500,
        } as unknown as ErrorResponse);
      }
    }

    const changePasswordDto: ChangePasswordDto = {
      client_id: configuration.AUTH0_CLIENTID,
      connection: 'Username-Password-Authentication',
      email: normalizedEmail,
    };

    try {
      const response = await UseAuth0Api({
        data: changePasswordDto,
        method: 'post',
        url: '/dbconnections/change_password',
      });

      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue(
        (error as AxiosError).response?.data as ErrorResponse,
      );
    }
  },
);

const initialUserState: UserSliceState = {
  allUsersByWorkspace: [],
  isAuthenticated: false,
  isLoading: ApiLoadingStateEnum.idle,
  isPasswordChanging: ApiLoadingStateEnum.idle,
  isUpdating: ApiLoadingStateEnum.idle,
  isUserLoading: ApiLoadingStateEnum.idle,
  isUserMeUpdating: ApiLoadingStateEnum.idle,
  isUsersLoading: ApiLoadingStateEnum.idle,
  users: [],
};

const usersSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(findAll.fulfilled, (state, action) => {
        state.isUsersLoading = ApiLoadingStateEnum.succeeded;

        const paginatedUser = action.payload as PaginatedUsers;

        state.users = paginatedUser.items;
        state.paginationMeta = paginatedUser.meta;
      })
      .addCase(findAll.pending, (state) => {
        state.isUsersLoading = ApiLoadingStateEnum.loading;
      })
      .addCase(findAll.rejected, (state, action) => {
        state.isUsersLoading = ApiLoadingStateEnum.failed;
        state.errorResponse = action.payload as ErrorResponse;
      })
      .addCase(findAllUsersByWorkspace.fulfilled, (state, action) => {
        state.isUsersLoading = ApiLoadingStateEnum.succeeded;

        const paginatedUser = action.payload as PaginatedUsers;

        state.allUsersByWorkspace = paginatedUser.items;
      })
      .addCase(findAllUsersByWorkspace.pending, (state) => {
        state.isUsersLoading = ApiLoadingStateEnum.loading;
      })
      .addCase(findAllUsersByWorkspace.rejected, (state, action) => {
        state.isUsersLoading = ApiLoadingStateEnum.failed;
        state.errorResponse = action.payload as ErrorResponse;
      })
      .addCase(findMe.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingStateEnum.succeeded;
        state.userMe = action.payload as User;
        state.isAuthenticated = true;
      })
      .addCase(findMe.pending, (state) => {
        state.isLoading = ApiLoadingStateEnum.loading;
      })
      .addCase(findMe.rejected, (state, action) => {
        state.isLoading = ApiLoadingStateEnum.failed;
        state.errorResponse = action.payload as ErrorResponse;
      })
      .addCase(updateMe.fulfilled, (state) => {
        state.isUserMeUpdating = ApiLoadingStateEnum.succeeded;
      })
      .addCase(updateMe.pending, (state) => {
        state.isUserMeUpdating = ApiLoadingStateEnum.loading;
      })
      .addCase(updateMe.rejected, (state, action) => {
        state.isUserMeUpdating = ApiLoadingStateEnum.failed;
        state.errorResponse = action.payload as ErrorResponse;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.isPasswordChanging = ApiLoadingStateEnum.succeeded;
      })
      .addCase(changePassword.pending, (state) => {
        state.isPasswordChanging = ApiLoadingStateEnum.loading;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.isPasswordChanging = ApiLoadingStateEnum.failed;
        state.errorResponse = action.payload as ErrorResponse;
      })
      .addCase(findOne.fulfilled, (state, action) => {
        state.isUserLoading = ApiLoadingStateEnum.succeeded;
        state.user = action.payload as User;
      })
      .addCase(findOne.pending, (state) => {
        state.isUserLoading = ApiLoadingStateEnum.loading;
      })
      .addCase(findOne.rejected, (state, action) => {
        state.isUserLoading = ApiLoadingStateEnum.failed;
        state.errorResponse = action.payload as ErrorResponse;
      })
      .addMatcher(isAnyOf(updateMe.pending, update.pending), (state) => {
        state.isUpdating = ApiLoadingStateEnum.loading;
        state.errorResponse = undefined;
      })
      .addMatcher(isAnyOf(updateMe.fulfilled, update.fulfilled), (state) => {
        state.isUpdating = ApiLoadingStateEnum.succeeded;
      })
      .addMatcher(
        isAnyOf(updateMe.rejected, update.rejected),
        (state, action) => {
          state.isUpdating = ApiLoadingStateEnum.failed;
          state.errorResponse = action.payload as ErrorResponse;
        },
      );
  },
  initialState: initialUserState,
  name: 'users',
  reducers: {
    reset: () => {
      return {
        ...initialUserState,
      };
    },
    resetErrorResponse: (state) => {
      return {
        ...state,
        errorResponse: initialUserState.errorResponse,
      };
    },
    resetLoadingState: (state) => {
      return {
        ...state,
        // Add more loading state here
        isUpdating: ApiLoadingStateEnum.idle,
        isUserMeUpdating: ApiLoadingStateEnum.idle,
      };
    },
    resetPasswordLoadingState: (state) => {
      return {
        ...state,
        // Add more loading state here
        isPasswordChanging: ApiLoadingStateEnum.idle,
      };
    },
    setReturnLocation: (state, action) => {
      return {
        ...state,
        returnLocation: action.payload,
      };
    },
    setUserMe: (state, action) => {
      state.isLoading = ApiLoadingStateEnum.succeeded;
      state.userMe = action.payload;
    },
  },
});

const userThunk = {
  changePassword: changePassword,
  findAll: findAll,
  findAllUsersByWorkspace,
  findMe: findMe,
  findOne: findOne,
  update: update,
  updateMe: updateMe,
};

const userSelectors = {
  allUsersByWorkspace: (state: RootState) => state.users.allUsersByWorkspace,
  errorResponse: (state: RootState) => state.users.errorResponse,
  isAuthenticated: (state: RootState) => state.users.isAuthenticated,
  isLoading: (state: RootState) => state.users.isLoading,
  isPasswordChanging: (state: RootState) => state.users.isPasswordChanging,
  isUpdating: (state: RootState) => state.users.isUpdating,
  isUserLoading: (state: RootState) => state.users.isUserLoading,
  isUserMeUpdating: (state: RootState) => state.users.isUserMeUpdating,
  isUsersLoading: (state: RootState) => state.users.isUsersLoading,
  paginationMeta: (state: RootState) => state.users.paginationMeta,
  returnLocation: (state: RootState) => state.users.returnLocation,
  user: (state: RootState) => state.users.user,
  userMe: (state: RootState) => state.users.userMe,
  users: (state: RootState) => state.users.users,
};

export const userService = {
  ...userThunk,
  actions: usersSlice.actions,
  selectors: userSelectors,
};

export default usersSlice.reducer;
